/**
 * Hardening for `return_url`, which arrives on an unauthenticated query string
 * (`/loginasadmin/authToken?return_url=...`) and is later assigned to
 * `window.location.href`.
 *
 * Two separate problems are being prevented:
 *
 *  1. `javascript:` / `data:` values execute in the portal's own origin, which
 *     lets a crafted link read `localStorage` (session token, session UID) and
 *     exfiltrate it — a one-click account takeover.
 *  2. Absolute URLs pointing at third-party hosts turn the endpoint into an
 *     open redirect, usable to launch phishing from a legitimate domain.
 *
 * The legitimate caller is the admin console, which passes its own
 * `window.location.href` so that an operator is returned there when "login as
 * customer" fails or that session is ended.
 *
 * The two sides of that flow have different shapes, which is what makes this
 * checkable at all:
 *
 *  - The *destination* is a customer portal, which is white-label and therefore
 *    on a different domain per tenant. It is never named here: it is whatever
 *    `window.location.origin` resolves to at runtime, so one code path is
 *    correct for every tenant with nothing to maintain per customer.
 *  - The *return target* is always the Anchanto admin console, so unlike the
 *    portal domains it is a known, bounded set and can be allow-listed.
 */

/**
 * Exact origins trusted in addition to the portal's own origin. Prefer this over
 * the domain list below when a specific admin console origin is known: it is
 * narrower, since it pins the scheme, host and port together.
 */
const TRUSTED_RETURN_ORIGINS: readonly string[] = [
    'http://localhost:4200',
    'http://127.0.0.1:4200'
];

/**
 * Domains whose hosts are trusted as return targets, over https only. Matches
 * the domain itself and any subdomain of it, so the admin console is covered in
 * every environment without enumerating each one.
 *
 * Keep this list to domains that are wholly controlled by us: every host under a
 * listed domain becomes a valid redirect target, so a forgotten or taken-over
 * subdomain becomes an open redirect. It deliberately does not include any
 * customer's white-label domain — a redirect between two tenants is never part
 * of this flow.
 */
const TRUSTED_RETURN_DOMAINS: readonly string[] = ['anchanto.com'];

/** Where the user is sent when `return_url` cannot be trusted. */
export const DEFAULT_RETURN_ROUTE = '/login';

/**
 * Resolves an untrusted `return_url` into something safe to assign to
 * `window.location.href`. Anything that cannot be positively proven safe
 * returns `fallback` — this fails closed by design.
 *
 * Call this both when persisting the value and when reading it back: values
 * captured before this validation existed may still be sitting in a user's
 * localStorage.
 */
export function sanitizeRedirectUrl(
    rawUrl: unknown,
    fallback: string = DEFAULT_RETURN_ROUTE
): string {
    if (typeof rawUrl !== 'string') {
        return fallback;
    }

    // Browsers strip control characters and whitespace while resolving a URL,
    // so `java\nscript:alert(1)` is a live payload. Remove them before any
    // scheme inspection rather than after.
    const candidate = rawUrl.replace(/[\u0000-\u0020\u007f]/g, '');
    if (!candidate) {
        return fallback;
    }

    // `//evil.com` and `/\evil.com` look like paths but are protocol-relative
    // and leave the origin, so they must be rejected before the path check.
    if (/^[/\\]{2}/.test(candidate)) {
        return fallback;
    }

    // A path rooted at "/" cannot leave this origin, so it is always safe.
    if (candidate.startsWith('/')) {
        return candidate;
    }

    let parsed: URL;
    try {
        parsed = new URL(candidate);
    } catch {
        // Neither a rooted path nor a parseable absolute URL. Refuse instead of
        // guessing at the author's intent.
        return fallback;
    }

    // The scheme check is what actually closes the XSS: `javascript:` and
    // `data:` parse successfully above, and are rejected here.
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
        return fallback;
    }

    return isTrustedOrigin(parsed) ? parsed.href : fallback;
}

function isTrustedOrigin(url: URL): boolean {
    if (
        typeof window !== 'undefined' &&
        window.location &&
        url.origin === window.location.origin
    ) {
        return true;
    }

    if (TRUSTED_RETURN_ORIGINS.includes(url.origin)) {
        return true;
    }

    // https only: an http return target would expose the admin's own session to
    // a network attacker, and downgrades are never needed here.
    if (url.protocol !== 'https:') {
        return false;
    }

    return TRUSTED_RETURN_DOMAINS.some((domain) => isHostWithin(url.hostname, domain));
}

/**
 * True when `hostname` is `domain` itself or a subdomain of it.
 *
 * The dot is required on the suffix branch on purpose. A bare
 * `hostname.endsWith('anchanto.com')` would also accept
 * `evil-anchanto.com` and `notanchanto.com`, which is the standard way this
 * style of check is bypassed.
 */
function isHostWithin(hostname: string, domain: string): boolean {
    const host = hostname.toLowerCase();
    const base = domain.toLowerCase();
    return host === base || host.endsWith(`.${base}`);
}
